/**
 * useRealtimeStatus — global Supabase realtime connection indicator.
 *
 * Most of OptiBay's 32 postgres_changes channels subscribe silently with no
 * status callback. The Supabase JS client retries automatically at the
 * WebSocket layer for transient blips, but for hard failures (proxy down,
 * network change, server-side disconnect) individual channel state may not
 * recover until the user navigates. This hook attaches a single lightweight
 * heartbeat channel and exposes its connection status so a global UI
 * indicator (in TopBar) can warn the operator.
 *
 * Pattern mirrors `useLiveReshuffle.ts:121-155` but at one heartbeat instead
 * of per-page channels, so adding it carries no per-page channel-budget
 * cost. The Supabase free tier allows 200 concurrent channels — this adds 1.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { uniqueChannelTopic } from '@/lib/realtime/floorRealtime';

export type RealtimeStatus = 'connecting' | 'connected' | 'disconnected';

export function useRealtimeStatus(): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>('connecting');

  useEffect(() => {
    let cancelled = false;
    // Stage 0D: one topic per instance. Pages remount TopBar (Birdseye swaps
    // its loading and loaded layouts), and a same-name channel still leaving
    // would be handed back dead — the indicator would never reach "connected".
    const channel = supabase
      .channel(uniqueChannelTopic('global-realtime-heartbeat'))
      .subscribe((s) => {
        if (cancelled) return;
        if (s === 'SUBSCRIBED') {
          setStatus('connected');
        } else if (s === 'CHANNEL_ERROR' || s === 'TIMED_OUT' || s === 'CLOSED') {
          // CLOSED also fires on intentional unmount; harmless because the
          // useEffect cleanup runs before this listener can dispatch.
          setStatus('disconnected');
        }
      });

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  return status;
}
